<template>
	<div class="pronto-alert"><slot name="alerts" :alerts="alerts" /></div>
</template>

<script>
import { getErrorTextFromCode } from '@/helperMethods'
import i18n from '@/i18n'

export const DEFAULT_TIME = 5000

const hashCode = (s) =>
	s.split('').reduce((a, b) => {
		a = (a << 5) - a + b.charCodeAt(0)
		return a & a
	}, 0)

export const alertTypes = {
	ERROR: 'error',
	SUCCESS: 'success',
	API: 'apiErrors',
}

const defaultTextByType = {
	[alertTypes.ERROR]: 'apierrors_error_generic',
	[alertTypes.SUCCESS]: 'common_success',
	[alertTypes.API]: 'apierrors_error_generic',
}

export default {
	name: 'Alert',
	data() {
		return { alertsById: {} }
	},
	methods: {
		showAlert({
			text,
			type = alertTypes.ERROR,
			closeable = true,
			autoClose = type === alertTypes.ERROR ? false : true,
			delay = DEFAULT_TIME,
		}) {
			if (!text) {
				const defaultText = defaultTextByType[type]
				text = defaultText ? i18n.global.t(defaultText) : text
				if (!text) return
			}
			const id = hashCode(text)
			const previousAlert = this.alertsById[id]
			if (previousAlert) {
				// cancel timeout if it was pending
				clearTimeout(previousAlert.timeout)
			}
			const newAlert = { id, text, type, autoClose, closeable, delay }
			newAlert.close = () => {
				// cancel timeout if it was pending
				clearTimeout(newAlert.timeout)
				delete this.alertsById[id]
			}
			if (autoClose) {
				newAlert.timeout = setTimeout(newAlert.close, delay)
			}
			this.alertsById[id] = newAlert
			return newAlert.close
		},
		detectErrorText(obj, data = {}) {
			// check if this is just a string
			if (!obj || typeof obj === 'string')
				return this.showAlert({ ...data, text: obj })
			// check if this is already in the alert format we expect
			if (obj.text && obj.type) return this.showAlert(data)
			// now check if this is an Error or Exception
			if (obj instanceof Error) {
				// this is an exception of some kind, probably from the API
				const { errors, error } = obj.response?.data || {}
				const values = errors && Object.values(errors)
				if (values?.length > 0) {
					// these are a list of multiple errors from bulk API actions
					// NOTE: these messages are already localized by the server
					return values
						.map(([{ message }]) => message)
						.filter((msg) => msg)
						.map((msg) => this.showAlert({ type: alertTypes.ERROR, text: msg }))
				}
				if (error) {
					// this is likely an error code, so we need to find a translation
					let text = this.$t(`apierrors_${error}`)
					if (text) {
						return this.showAlert({ type: alertTypes.ERROR, text })
					}
				}
				if (obj.message) {
					const codedMessage = getErrorTextFromCode(obj.message)
					if (codedMessage) {
						const text = this.$t(codedMessage)
						return this.showAlert({ type: alertTypes.ERROR, text })
					}
					// this is likely a generic JS Error object
					return this.showAlert({ type: alertTypes.ERROR, text: obj.message })
				}
			}
			// as a last resort, we'll just display a generic error
			return this.showAlert({ type: alertTypes.ERROR })
		},
	},
	computed: {
		alerts() {
			return Object.values(this.alertsById)
		},
		alertMethods() {
			const methods = Object.values(alertTypes).reduce((acc, type) => {
				acc[type] = (text, data = {}) =>
					this.detectErrorText(text, { ...data, type })
				return acc
			}, {})
			methods[alertTypes.API] = (e, data) => {
				if (!e?.response?.data?.errors) {
					return methods[alertTypes.ERROR](null, data)
				}
				Object.values(e.response.data.errors)
					.map(([{ message }]) => message)
					.forEach((msg) => {
						return methods[alertTypes.ERROR](msg, data)
					})
			}
			return methods
		},
	},
}
</script>

<style lang="scss" scoped></style>
